import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import { GridLoader } from "react-spinners";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { buscarSumarizacaoIcmsApuracao, buscarLancamentosCat42Apuracao } from "../../../services/apuracao.service";
import Pagination from "../../../components/Pagination";
import ApuracaoCardShell from "../components/ApuracaoCardShell";
import BreadcrumbApuracao from "../components/BreadcrumbApuracao";
import SelectFiltro, { BuscaFiltro } from "../components/SelectFiltro";
import { formatarCnpj, fmtMesAno } from "../utils";
import { formatarMoedaBRL } from "../Detalhe/utils/calcularTotaisPorAno.util";
import { formatarQuantidadeLancamentos } from "../Detalhe/utils/formatarQuantidade.util";
import styles from "../Detalhe/detalhe.styles.module.css";
import tableStyles from "../../../components/TableCard/TableView/styles.module.css";

const CODLEGAL_OPCOES = [
  { value: "", label: "Todos" },
  { value: "0", label: "0 — Comercialização subsequente" },
  { value: "1", label: "1 — Consumidor final" },
  { value: "2", label: "2 — Fato gerador não realizado" },
  { value: "3", label: "3 — Isenção / não incidência" },
  { value: "4", label: "4 — Outro Estado" },
];

const INDOPER_OPCOES = [
  { value: "", label: "Todos" },
  { value: "0", label: "0 — Entrada" },
  { value: "1", label: "1 — Saída" },
];

function agruparPorItem(linhas) {
  const porItem = new Map();
  for (const linha of linhas) {
    const chave = linha.codItem || "";
    if (!porItem.has(chave)) {
      porItem.set(chave, {
        codItem: linha.codItem,
        descricaoItem: linha.descricaoItem,
        ncm: linha.ncm,
        cest: linha.cest,
        ean: linha.ean,
        unidade: linha.unidade,
        aliqIcms: linha.aliqIcms,
        qtdIni: linha.qtdIni,
        icmsTotIni: linha.icmsTotIni,
        qtdFim: linha.qtdFim,
        icmsTotFim: linha.icmsTotFim,
        movimentos: [],
        ressarcimento: 0,
        complemento: 0,
      });
    }
    const acc = porItem.get(chave);
    acc.movimentos.push(linha);
    acc.ressarcimento += Number(linha.vlRessarcimento) || 0;
    acc.complemento += Number(linha.vlComplemento) || 0;
  }
  return Array.from(porItem.values()).sort((a, b) =>
    (a.codItem || "").localeCompare(b.codItem || "")
  );
}

function exportarCsv(itens, nomeArquivo) {
  const linhas = [
    [
      "cod_item", "descricao_item", "ncm", "competencia", "reg", "data", "chave_doc",
      "num_item", "ind_oper", "cfop", "modelo", "qtd", "icms_tot", "vl_confr", "cod_legal",
      "ressarcimento", "complemento", "valor_produto", "aliq_icms",
      "vl_icms_proprio", "vl_mva", "vl_bc_icms_st", "aliq_icms_st", "vl_icms_st_bruto",
      "vl_icms_st", "vl_total_nota", "vl_suportado_entrada", "qtd_entrada_coberta",
    ].join(";"),
  ];
  for (const item of itens) {
    for (const m of item.movimentos) {
      linhas.push(
        [
          item.codItem, item.descricaoItem, item.ncm, m.competencia, m.reg, m.dtMovimentacao,
          m.chaveDoc, m.numeroItem ?? "", m.indOper, m.cfop, m.modelo, m.qtd, m.icmsTot ?? "",
          m.vlConfr ?? "", m.codLegal ?? "", m.vlRessarcimento ?? "", m.vlComplemento ?? "",
          m.valorProduto ?? "", item.aliqIcms ?? "",
          m.vlIcmsProprio ?? "", m.vlMva ?? "", m.vlBcIcmsSt ?? "", m.aliqIcmsSt ?? "",
          m.vlIcmsStBruto ?? "", m.vlIcmsSt ?? "", m.vlTotalNota ?? "",
          m.vlSuportadoEntrada ?? "", m.qtdEntradaCoberta ?? "",
        ]
          .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
          .join(";")
      );
    }
  }
  const blob = new Blob([linhas.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nomeArquivo;
  a.click();
  URL.revokeObjectURL(url);
}

export default function LancamentosCat42() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const competenciaInicial = searchParams.get("competencia") || "";

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [apuracao, setApuracao] = useState(null);
  const [competencia, setCompetencia] = useState(competenciaInicial);
  const [competenciasDisponiveis, setCompetenciasDisponiveis] = useState([]);
  const [linhas, setLinhas] = useState([]);
  const [filtroCodItem, setFiltroCodItem] = useState("");
  const [filtroCodLegal, setFiltroCodLegal] = useState("");
  const [filtroIndOper, setFiltroIndOper] = useState("");
  const [itensPagina, setItensPagina] = useState([]);

  useEffect(() => {
    let ativo = true;
    async function carregarApuracaoECompetencias() {
      const res = await buscarSumarizacaoIcmsApuracao(id);
      if (!ativo || !res.sucesso) return;
      setApuracao(res.apuracao || null);
      const ymSet = new Set(
        (res.linhas || []).map((l) => String(l.competencia || "").slice(0, 7))
      );
      setCompetenciasDisponiveis(Array.from(ymSet).sort());
    }
    carregarApuracaoECompetencias();
    return () => {
      ativo = false;
    };
  }, [id]);

  const carregarLancamentos = useCallback(async () => {
    if (!competencia) {
      setLoading(false);
      setErro("Selecione uma competência.");
      return;
    }
    setLoading(true);
    setErro("");
    const res = await buscarLancamentosCat42Apuracao(id, competencia);
    setLoading(false);
    if (!res.sucesso) {
      setErro(res.mensagem || "Não foi possível carregar os lançamentos.");
      return;
    }
    setLinhas(res.linhas);
  }, [id, competencia]);

  useEffect(() => {
    carregarLancamentos();
  }, [carregarLancamentos]);

  function handleCompetenciaChange(novaCompetencia) {
    setCompetencia(novaCompetencia);
    setSearchParams({ competencia: novaCompetencia });
  }

  function limparFiltros() {
    setFiltroCodItem("");
    setFiltroCodLegal("");
    setFiltroIndOper("");
  }

  const competenciaOpcoes = useMemo(() => {
    const base = competenciasDisponiveis.map((ym) => ({ value: ym, label: fmtMesAno(ym) }));
    if (competencia && !competenciasDisponiveis.includes(competencia)) {
      return [{ value: competencia, label: fmtMesAno(competencia) }, ...base];
    }
    return base;
  }, [competenciasDisponiveis, competencia]);

  const linhasFiltradas = useMemo(() => {
    return linhas.filter((l) => {
      if (filtroCodItem && !String(l.codItem || "").toLowerCase().includes(filtroCodItem.toLowerCase())) {
        return false;
      }
      if (filtroCodLegal && String(l.codLegal ?? "") !== filtroCodLegal) return false;
      if (filtroIndOper && l.indOper !== filtroIndOper) return false;
      return true;
    });
  }, [linhas, filtroCodItem, filtroCodLegal, filtroIndOper]);

  const itens = useMemo(() => agruparPorItem(linhasFiltradas), [linhasFiltradas]);

  return (
    <BreadcrumbApuracao
      itens={[
        { label: "Apurações", onClick: () => navigate("/relatorios/apuracoes") },
        {
          // TXM-418 — `id` aqui é o id da apuração FILHA (ICMS_ST_CAT42), usado
          // pra buscar os próprios dados desta tela. Pra "voltar", precisa do id
          // do PAI (apuração ICMS unificada com abas) — sem isso, caía na tela
          // standalone antiga do CAT-42 (achado real 2026-09-18, sem abas,
          // "Apuração #<id-da-filha>" em vez da apuração ICMS unificada).
          label: `Apuração ICMS — ${apuracao?.empresa || "—"}`,
          onClick: () =>
            navigate(
              `/relatorios/apuracoes/${apuracao?.idApuracaoPai || id}/detalhe/icms?tab=cat42`
            ),
        },
        { label: "Lançamentos CAT-42" },
      ]}
    >
      <Alert severity="info" sx={{ mb: 2 }}>
        Cada bloco agrupa um <strong>COD_ITEM</strong> (Ficha 2 / registro 0200). O cabeçalho do
        bloco exibe o saldo do registro 1050. A grade lista os movimentos no leiaute de
        conferência dos registros 1100/1200 e das colunas da Ficha 3 (ICMS_TOT, VL_CONFR,
        COD_LEGAL, ressarcimento e complemento), mais o cálculo ICMS-ST por item (tags do
        grupo ICMSST da NF-e). "ICMS ST suportado (entrada vinc.)" e "Qtd. entrada coberta"
        (só em linhas de saída) já vêm do rateio por média ponderada quando uma saída
        consumiu mais de uma entrada — calculado pelo motor oficial, não recalculado aqui.
      </Alert>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      {/* TXM-478 — "Legenda de CFOPs (entrada/saída + exclusão do 5409)". Lista fixa (não
          derivada dos dados carregados): são os CFOPs de ICMS-ST do arquivo digital
          (planilha "Cat 42 _ Depara.xlsx", aba "Calculo ICMS ST"), não os CFOPs que
          aparecem nesta apuração específica — REQUISITO EXPLÍCITO do card TXM-478, seção
          "1.6 CFOPs de ICMS-ST (arquivo digital)" e "2.4 Outras alterações na tela", com
          esta lista exata como fonte.
          REVERTIDO (achado 2026-09-14): cheguei a trocar esses números pela whitelist real
          do motor de cálculo (icmsstcat42_preencher.sql), achando que a lista da planilha
          estava desatualizada/enganosa — mas o card especifica ESTA lista literalmente como
          requisito, citando a planilha como fonte. A whitelist do motor governa o CÁLCULO
          (isso já foi confirmado antes, ver icmsstcat42_consultar_lancamentos.sql); esta
          legenda é uma referência do arquivo digital, propósito diferente — não precisam
          bater. Revertido pra não contrariar o card. */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mb: 2, fontSize: "0.8rem", color: "#555" }}>
        <strong style={{ color: "#0d2847" }}>CFOPs de ICMS-ST:</strong>
        <span>Entrada: 1403, 2403, 1409, 2409, 1411, 2411</span>
        <span>·</span>
        <span>Saída: 5405, 6404, 6409, 6102, 6108, 6152, 5411, 6411</span>
        <span>·</span>
        <span style={{ opacity: 0.6 }}>CFOP 5409/6409 (transferência) são excluídos do cálculo — linhas esmaecidas abaixo</span>
      </Box>

      <ApuracaoCardShell
        hideContainer
        innerCardSx={{ border: "none", margin: 0 }}
        conteudoComScrollProprio
        Icone={Inventory2Icon}
        titulo="Lançamentos CAT-42 — por ID de produto"
        subtitulo={
          <>
            {apuracao?.empresa || "—"} · {formatarCnpj(apuracao?.cnpj)}
            <br />
            Competência: {competencia ? fmtMesAno(competencia) : "—"} · Conferência Ficha 3 /
            registros 1050-1100-1200
          </>
        }
        acaoHeader={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography sx={{ color: "#666", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                {itens.length} itens no filtro
              </Typography>
              <Button
                variant="contained"
                size="small"
                disabled={itens.length === 0}
                onClick={() =>
                  exportarCsv(itens, `lancamentos_cat42_${formatarCnpj(apuracao?.cnpj)}_${competencia}.csv`)
                }
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, bgcolor: "#18316e", minHeight: 40, whiteSpace: "nowrap", px: 2, "&:hover": { bgcolor: "#152858" } }}
              >
                Exportar CSV
              </Button>
            </Box>
          }
          filtros={
            <>
              <BuscaFiltro
                label="ID / código do produto"
                placeholder="Ex.: ITEM-85044040"
                value={filtroCodItem}
                onChange={setFiltroCodItem}
              />
              <SelectFiltro label="Competência" value={competencia} onChange={handleCompetenciaChange} options={competenciaOpcoes} />
              <SelectFiltro label="COD_LEGAL" value={filtroCodLegal} onChange={setFiltroCodLegal} options={CODLEGAL_OPCOES} />
              <SelectFiltro label="IND_OPER" value={filtroIndOper} onChange={setFiltroIndOper} options={INDOPER_OPCOES} />
              <Button
                variant="outlined"
                size="small"
                onClick={limparFiltros}
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", minHeight: 40, color: "#18316e", borderColor: "#18316e", "&:hover": { borderColor: "#152858", bgcolor: "rgba(24, 49, 110, 0.04)" } }}
              >
                Limpar filtros
              </Button>
            </>
          }
          paginacao={
            !loading &&
            itens.length > 0 && (
              <Pagination
                paginationKey="lancamentos-cat42"
                data={itens}
                filteredData={itensPagina}
                setFilteredData={setItensPagina}
              />
            )
          }
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <GridLoader color="#1565c0" size={10} />
            </Box>
          ) : itens.length === 0 && !erro ? (
            <p className={styles.emptyMsg}>Nenhum lançamento encontrado com os filtros atuais.</p>
          ) : (
            <div style={{ padding: "0 20px" }}>
              {itensPagina.map((item) => (
                <div key={item.codItem || "sem-item"} style={{ marginBottom: 28 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
                    <h3 style={{ margin: 0, fontSize: 16, color: "#0d2847" }}>
                      {item.codItem || "(sem código)"} — {item.descricaoItem || "(sem descrição)"}
                    </h3>
                    <span style={{ fontSize: 13, color: "#555" }}>
                      Movimentos: <strong>{item.movimentos.length}</strong> · Ressarcimento:{" "}
                      <strong className={styles.numRessarcimento}>{formatarMoedaBRL(item.ressarcimento)}</strong> ·
                      Complemento: <strong className={styles.numComplemento}>{formatarMoedaBRL(item.complemento)}</strong>
                    </span>
                  </div>
                  <p style={{ margin: "4px 0 10px", fontSize: 12, color: "#777", fontFamily: "ui-monospace, monospace" }}>
                    NCM {item.ncm || "—"} · EAN {item.ean || "—"} · UNID {item.unidade || "—"} · CEST{" "}
                    {item.cest || "—"} · ALÍQ {item.aliqIcms != null ? `${item.aliqIcms}%` : "—"}
                  </p>

                  <div className={styles.dashGrid} style={{ gridTemplateColumns: "repeat(5, minmax(0,1fr))", marginBottom: 10 }}>
                    <div className={styles.dashCard}>
                      <div className={styles.dashLbl}>1050 COD_ITEM</div>
                      <div className={styles.dashVal} style={{ fontSize: 13 }}>{item.codItem || "—"}</div>
                    </div>
                    {/* TXM-478 — qtdIni/icmsTotIni/qtdFim/icmsTotFim vêm NULL (não 0) quando o
                        item não tem nenhuma linha de saldo em z_{cnpj}_{ano}_icmsstcat42 (só
                        entrada, sem saída no período — saldo final ainda não apurado pelo
                        motor, ver icmsstcat42_preencher.sql). "0"/"R$ 0,00" implicaria
                        estoque zerado, que não é o caso; "Não apurado" é o estado real. */}
                    <div className={styles.dashCard}>
                      <div className={styles.dashLbl}>1050 QTD_INI</div>
                      <div className={styles.dashVal}>
                        {item.qtdIni != null ? formatarQuantidadeLancamentos(item.qtdIni) : "Não apurado"}
                      </div>
                    </div>
                    <div className={styles.dashCard}>
                      <div className={styles.dashLbl}>1050 ICMS_TOT_INI</div>
                      <div className={styles.dashVal}>
                        {item.icmsTotIni != null ? formatarMoedaBRL(item.icmsTotIni) : "Não apurado"}
                      </div>
                    </div>
                    <div className={styles.dashCard}>
                      <div className={styles.dashLbl}>1050 QTD_FIM</div>
                      <div className={styles.dashVal}>
                        {item.qtdFim != null ? formatarQuantidadeLancamentos(item.qtdFim) : "Não apurado"}
                      </div>
                    </div>
                    <div className={styles.dashCard}>
                      <div className={styles.dashLbl}>1050 ICMS_TOT_FIM</div>
                      <div className={styles.dashVal}>
                        {item.icmsTotFim != null ? formatarMoedaBRL(item.icmsTotFim) : "Não apurado"}
                      </div>
                    </div>
                  </div>

                  <div style={{ overflow: "auto" }}>
                    <table className={tableStyles.accordion_table}>
                      <thead>
                        <tr>
                          <th scope="col">Competência</th>
                          <th scope="col">REG</th>
                          <th scope="col">DATA</th>
                          <th scope="col">CHV_DOC / ECF</th>
                          <th scope="col" style={{ textAlign: "right" }}>NUM_ITEM</th>
                          <th scope="col">IND_OPER</th>
                          <th scope="col">CFOP</th>
                          <th scope="col">Modelo</th>
                          <th scope="col" style={{ textAlign: "right" }}>QTD</th>
                          <th scope="col" style={{ textAlign: "right" }}>ICMS_TOT</th>
                          <th scope="col" style={{ textAlign: "right" }}>VL_CONFR</th>
                          <th scope="col">COD_LEGAL</th>
                          <th scope="col" style={{ textAlign: "right" }}>Ressarc. (25)</th>
                          <th scope="col" style={{ textAlign: "right" }}>Compl. (26)</th>
                          <th scope="col" style={{ textAlign: "right" }}>Valor Produto</th>
                          <th scope="col" style={{ textAlign: "right" }}>Alíq. ICMS</th>
                          <th scope="col" style={{ textAlign: "right" }}>ICMS Próprio/Saída</th>
                          <th scope="col" style={{ textAlign: "right" }}>MVA/IVA</th>
                          <th scope="col" style={{ textAlign: "right" }}>BC ICMS ST</th>
                          <th scope="col" style={{ textAlign: "right" }}>Alíq. ICMS ST</th>
                          <th scope="col" style={{ textAlign: "right" }}>ICMS ST bruto</th>
                          <th scope="col" style={{ textAlign: "right" }}>ICMS ST</th>
                          <th scope="col" style={{ textAlign: "right" }}>Total da Nota</th>
                          <th scope="col" style={{ textAlign: "right" }}>ICMS ST suportado (entrada vinc.)</th>
                          <th scope="col" style={{ textAlign: "right" }}>Qtd. entrada coberta</th>
                        </tr>
                      </thead>
                      <tbody>
                        {item.movimentos.map((m, i) => {
                          // TXM-478 — CFOP 5409/6409 são transferência entre estabelecimentos
                          // do mesmo grupo, excluídos do cálculo de ICMS-ST (código 0, ver
                          // icmsstcat42_preencher.sql — `WHEN d.cfop_saida::text IN ('5409',
                          // '6409') THEN '0'`); o card pede a linha esmaecida e as colunas de
                          // cálculo em branco, não só os campos que o motor já zera (Ressarc./
                          // Compl./VL_CONFR) — as colunas "Valor Produto" em diante vêm de
                          // dado bruto de entrada/saída, então precisam ser blanqueadas aqui,
                          // não vêm nulas do banco.
                          //
                          // BUG CORRIGIDO (achado 2026-09-11): só "5409" era tratado — CFOP
                          // 6409 (mesma transferência, só que interestadual) tinha sua linha
                          // mostrada normal, com os valores de cálculo preenchidos, quando na
                          // verdade o motor já zera os dois igualmente.
                          const isTransferenciaExcluida = m.cfop === "5409" || m.cfop === "6409";
                          const calculo = (valor) => (isTransferenciaExcluida ? "—" : valor);
                          return (
                          <tr key={`${m.chaveDoc}-${m.numeroItem}-${i}`} style={isTransferenciaExcluida ? { opacity: 0.5 } : undefined}>
                            <td>{m.competencia}</td>
                            <td>{m.reg}</td>
                            <td>{m.dtMovimentacao ? m.dtMovimentacao.split("-").reverse().join("/") : "—"}</td>
                            <td style={{ fontFamily: "ui-monospace, monospace", fontSize: 11 }}>
                              {m.chaveDoc || "—"}
                            </td>
                            <td style={{ textAlign: "right" }}>{m.numeroItem ?? "—"}</td>
                            <td>
                              <span
                                style={{
                                  padding: "2px 8px",
                                  borderRadius: 12,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  background: m.indOper === "0" ? "#e3f2fd" : "#fff3e0",
                                  color: m.indOper === "0" ? "#1565c0" : "#e65100",
                                }}
                              >
                                {m.indOper === "0" ? "0 Entrada" : "1 Saída"}
                              </span>
                            </td>
                            <td>{m.cfop}</td>
                            <td>{m.modelo}</td>
                            <td style={{ textAlign: "right" }}>{formatarQuantidadeLancamentos(m.qtd)}</td>
                            <td style={{ textAlign: "right" }}>{m.icmsTot != null ? formatarMoedaBRL(m.icmsTot) : "—"}</td>
                            <td style={{ textAlign: "right" }}>{m.vlConfr != null ? formatarMoedaBRL(m.vlConfr) : "—"}</td>
                            <td>
                              {m.codLegal != null ? (
                                <span
                                  style={{
                                    padding: "2px 8px",
                                    borderRadius: 12,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    background: "#e8f5e9",
                                    color: "#2e7d32",
                                  }}
                                >
                                  {m.codLegal}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td style={{ textAlign: "right", color: "#2e7d32" }}>
                              {m.vlRessarcimento != null ? formatarMoedaBRL(m.vlRessarcimento) : "—"}
                            </td>
                            <td style={{ textAlign: "right", color: "#c62828" }}>
                              {m.vlComplemento != null ? formatarMoedaBRL(m.vlComplemento) : "—"}
                            </td>
                            <td style={{ textAlign: "right" }}>
                              {calculo(m.valorProduto != null ? formatarMoedaBRL(m.valorProduto) : "—")}
                            </td>
                            <td style={{ textAlign: "right" }}>{calculo(item.aliqIcms != null ? `${item.aliqIcms}%` : "—")}</td>
                            <td style={{ textAlign: "right" }}>
                              {calculo(m.vlIcmsProprio != null ? formatarMoedaBRL(m.vlIcmsProprio) : "—")}
                            </td>
                            <td style={{ textAlign: "right" }}>{calculo(m.vlMva != null ? `${m.vlMva}%` : "—")}</td>
                            <td style={{ textAlign: "right" }}>
                              {calculo(m.vlBcIcmsSt != null ? formatarMoedaBRL(m.vlBcIcmsSt) : "—")}
                            </td>
                            <td style={{ textAlign: "right" }}>{calculo(m.aliqIcmsSt != null ? `${m.aliqIcmsSt}%` : "—")}</td>
                            <td style={{ textAlign: "right" }}>
                              {calculo(m.vlIcmsStBruto != null ? formatarMoedaBRL(m.vlIcmsStBruto) : "—")}
                            </td>
                            <td style={{ textAlign: "right" }}>
                              {calculo(m.vlIcmsSt != null ? formatarMoedaBRL(m.vlIcmsSt) : "—")}
                            </td>
                            <td style={{ textAlign: "right" }}>
                              {calculo(m.vlTotalNota != null ? formatarMoedaBRL(m.vlTotalNota) : "—")}
                            </td>
                            <td style={{ textAlign: "right" }}>
                              {calculo(m.vlSuportadoEntrada != null ? formatarMoedaBRL(m.vlSuportadoEntrada) : "—")}
                            </td>
                            <td style={{ textAlign: "right" }}>
                              {calculo(m.qtdEntradaCoberta != null ? formatarQuantidadeLancamentos(m.qtdEntradaCoberta) : "—")}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
      </ApuracaoCardShell>
    </BreadcrumbApuracao>
  );
}
